// /admin/assign — match approved residents with vetted mowkins.
// Reachable via direct URL; requires admin role (server-side gate).
//
// Why a separate page from Admin.tsx: assignment is a workflow page,
// not just another table. Splitting keeps Admin.tsx legible while this
// page owns the date-picking + matching logic.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AdminNav } from '../components/AdminNav';

type YardSize = 'small' | 'medium' | 'large' | 'xl';

interface ApprovedResident {
  id: number;
  first_name: string;
  last_name: string;
  address: string;
  city: string;
  county: string;
  yard_size: YardSize | null;
  availability_json: string | null;
  cycle_end: string | null;
  assigned_track: 'mowkin' | 'volunteer' | null;
}
interface ReadyMowkin {
  kind: 'volunteer' | 'provider';
  id: number;
  label: string;
  city: string | null;
  county: string | null;
  max_yard_size: YardSize | null;
  availability_json: string | null;
  service_radius_miles: number | null;
  preferred_county: string | null;
}
interface AssignmentRow {
  id: number;
  scheduled_date: string;
  time_window: string | null;
  status: string;
  admin_notes: string | null;
  resident_id: number;
  volunteer_id: number | null;
  provider_id: number | null;
  resident_name: string | null;
  resident_address: string | null;
  resident_city: string | null;
  mowkin_label: string | null;
  mowkin_kind: 'volunteer' | 'provider' | null;
}

const YARD_RANK: Record<YardSize, number> = { small: 1, medium: 2, large: 3, xl: 4 };

function parseAvail(raw: string | null) {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as {
      weekdays: string[];
      windows: string[];
      blackout_dates: string[];
      notes: string;
    };
  } catch { return null; }
}

function fitScore(resident: ApprovedResident, mowkin: ReadyMowkin): number {
  let score = 0;
  // Yard size fit
  if (resident.yard_size && mowkin.max_yard_size) {
    if (YARD_RANK[resident.yard_size] <= YARD_RANK[mowkin.max_yard_size]) score += 40;
    else score -= 20;
  }
  // County preference
  if (mowkin.preferred_county && resident.county) {
    if (mowkin.preferred_county === resident.county) score += 25;
  } else {
    score += 10; // No preference = neutral
  }
  // Availability overlap
  const ra = parseAvail(resident.availability_json);
  const ma = parseAvail(mowkin.availability_json);
  if (ra && ma) {
    const dayOverlap = ra.weekdays.filter((d) => ma.weekdays.includes(d)).length;
    const winOverlap = ra.windows.filter((w) => ma.windows.includes(w)).length;
    score += dayOverlap * 5;
    score += winOverlap * 5;
  }
  return score;
}

export function AdminAssignPage() {
  const { user } = useAuth();
  const [residents, setResidents] = useState<ApprovedResident[]>([]);
  const [mowkins, setMowkins] = useState<ReadyMowkin[]>([]);
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedResident, setSelectedResident] = useState<number | null>(null);
  const [selectedMowkin, setSelectedMowkin]   = useState<string | null>(null); // "v:12" or "p:3"
  const [date, setDate] = useState('');
  const [timeWindow, setTimeWindow] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [posting, setPosting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      // One endpoint returns approved residents + vetted volunteers + approved providers
      // with the scheduling columns already selected. Plus a separate call for
      // existing assignments to show the "recent" section.
      const [candR, assignR] = await Promise.all([
        fetch('/api/admin/assignment-candidates', { credentials: 'include' }),
        fetch('/api/admin/assignments', { credentials: 'include' }),
      ]);
      const candJ = await candR.json() as {
        ok: boolean;
        data?: {
          residents: ApprovedResident[];
          volunteers: (ReadyMowkin & { first_name?: string; last_name?: string })[];
          providers: (ReadyMowkin & { business_name?: string })[];
        };
        error?: string;
      };
      const assignJ = await assignR.json() as {
        ok: boolean;
        data?: { assignments: AssignmentRow[] };
        error?: string;
      };

      if (!candJ.ok) throw new Error(candJ.error || 'candidates fetch failed');

      setResidents(candJ.data?.residents || []);
      const vol = (candJ.data?.volunteers || []).map((v) => ({
        ...v,
        kind: 'volunteer' as const,
        label: `${v.first_name || ''} ${v.last_name || ''}`.trim() || `Volunteer #${v.id}`,
      }));
      const prov = (candJ.data?.providers || []).map((p) => ({
        ...p,
        kind: 'provider' as const,
        label: p.business_name || `Provider #${p.id}`,
      }));
      setMowkins([...vol, ...prov]);
      setAssignments(assignJ.data?.assignments || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);

  const sortedMowkins = useMemo(() => {
    if (!selectedResident) return mowkins;
    const r = residents.find((x) => x.id === selectedResident);
    if (!r) return mowkins;
    return [...mowkins].sort((a, b) => fitScore(r, b) - fitScore(r, a));
  }, [selectedResident, residents, mowkins]);

  const selectedResidentRow = residents.find((r) => r.id === selectedResident) || null;
  const selectedMowkinRow = useMemo(() => {
    if (!selectedMowkin) return null;
    const [k, id] = selectedMowkin.split(':');
    return mowkins.find((m) => m.kind === k && String(m.id) === id) || null;
  }, [selectedMowkin, mowkins]);

  const submit = async () => {
    if (!selectedResident || !selectedMowkin || !date) {
      setMsg('Pick a resident, a mowkin, and a date.');
      return;
    }
    setPosting(true); setMsg(null);
    try {
      const [kind, idStr] = selectedMowkin.split(':');
      const id = Number(idStr);
      const body: Record<string, unknown> = {
        resident_id: selectedResident,
        scheduled_date: date,
        time_window: timeWindow || undefined,
        admin_notes: notes || undefined,
      };
      if (kind === 'volunteer') body.volunteer_id = id;
      else                       body.provider_id  = id;

      const res = await fetch('/api/admin/assignments', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await res.json() as { ok: boolean; error?: string };
      if (!j.ok) throw new Error(j.error || 'Assignment failed');
      setMsg('Assignment created. The mowkin has been emailed.');
      setSelectedResident(null); setSelectedMowkin(null);
      setDate(''); setTimeWindow(''); setNotes('');
      void load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Assignment failed');
    } finally {
      setPosting(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <>
        <AdminNav />
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-terracotta text-sm">
            Admin only.
          </div>
        </div>
      </>
    );
  }
  if (loading) {
    return (
      <>
        <AdminNav />
        <div className="max-w-5xl mx-auto px-6 py-16 text-forest/60 italic">Loading...</div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <AdminNav />
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-terracotta text-sm">{error}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNav />
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
      <div className="text-xs tracking-[0.25em] uppercase text-terracotta mb-3">Admin · Matching</div>
      <h1 className="text-4xl text-forest mb-2 font-display" style={{ fontVariationSettings: '"opsz" 144' }}>
        Assign a mow.
      </h1>
      <p className="text-forest/70 mb-8">
        Pick an approved resident, the best-fit mowkin, set a date, send.
        <Link to="/admin" className="text-terracotta hover:underline ml-3">← back to admin</Link>
      </p>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Residents */}
        <div className="bg-cream border border-forest/15 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-display text-forest">Approved residents ({residents.length})</h2>
          </div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {residents.length === 0 && <p className="text-sm text-forest/60 italic">No approved residents.</p>}
            {residents.map((r) => {
              const active = selectedResident === r.id;
              const av = parseAvail(r.availability_json);
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelectedResident(r.id)}
                  className={`block w-full text-left border p-3 transition-colors ${
                    active ? 'border-terracotta bg-terracotta/5' : 'border-forest/15 hover:border-terracotta'
                  }`}
                >
                  <div className="text-sm font-medium text-forest">
                    {r.first_name} {r.last_name} <span className="text-forest/50">#{r.id}</span>
                  </div>
                  <div className="text-xs text-forest/70">{r.address}, {r.city}, {r.county}</div>
                  <div className="flex flex-wrap gap-3 text-[11px] uppercase tracking-[0.1em] text-forest/60 mt-1">
                    <span>Yard: {r.yard_size || '—'}</span>
                    <span>Days: {av?.weekdays.join('/') || '—'}</span>
                    <span>Cycle ends: {r.cycle_end || '—'}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Mowkins */}
        <div className="bg-cream border border-forest/15 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-display text-forest">Ready mowkins ({mowkins.length})</h2>
            {selectedResident && <span className="text-[10px] uppercase tracking-[0.15em] text-terracotta">sorted by fit</span>}
          </div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {mowkins.length === 0 && <p className="text-sm text-forest/60 italic">No vetted mowkins yet.</p>}
            {sortedMowkins.map((m) => {
              const key = `${m.kind}:${m.id}`;
              const active = selectedMowkin === key;
              const fit = selectedResidentRow ? fitScore(selectedResidentRow, m) : null;
              const av = parseAvail(m.availability_json);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedMowkin(key)}
                  className={`block w-full text-left border p-3 transition-colors ${
                    active ? 'border-terracotta bg-terracotta/5' : 'border-forest/15 hover:border-terracotta'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.15em] bg-forest/10 text-forest px-1.5 py-0.5">{m.kind}</span>
                    <span className="text-sm font-medium text-forest">{m.label}</span>
                    {fit !== null && (
                      <span className={`text-[10px] uppercase tracking-[0.15em] ml-auto px-2 py-0.5 ${
                        fit >= 50 ? 'bg-forest text-cream' : fit >= 20 ? 'bg-sand text-forest' : 'bg-forest/10 text-forest/60'
                      }`}>fit {fit}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 text-[11px] uppercase tracking-[0.1em] text-forest/60 mt-1">
                    <span>Max yard: {m.max_yard_size || '—'}</span>
                    <span>Radius: {m.service_radius_miles ?? '—'} mi</span>
                    <span>County: {m.preferred_county || '—'}</span>
                    <span>Days: {av?.weekdays.join('/') || '—'}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Schedule form */}
      <div className="bg-cream border border-forest/15 p-5 mb-10">
        <h2 className="text-lg font-display text-forest mb-3">Schedule the mow</h2>
        <div className="text-sm text-forest/70 mb-4">
          {selectedResidentRow ? (
            <>Resident: <span className="text-forest">{selectedResidentRow.first_name} {selectedResidentRow.last_name}</span> · </>
          ) : 'Pick a resident · '}
          {selectedMowkinRow ? (
            <>Mowkin: <span className="text-forest">{selectedMowkinRow.label}</span></>
          ) : 'Pick a mowkin'}
        </div>
        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-1">Date</div>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-terracotta"
            />
          </div>
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-1">Time window</div>
            <select
              value={timeWindow}
              onChange={(e) => setTimeWindow(e.target.value)}
              className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-terracotta"
            >
              <option value="">No preference</option>
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
              <option value="evening">Evening</option>
            </select>
          </div>
        </div>
        <div className="mb-3">
          <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-1">Admin notes (visible to mowkin)</div>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-terracotta"
          />
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={posting || !selectedResident || !selectedMowkin || !date}
          className="text-xs tracking-[0.15em] uppercase bg-forest text-cream px-5 py-2 hover:bg-terracotta transition-colors disabled:opacity-40"
        >
          {posting ? 'Sending...' : 'Create assignment'}
        </button>
        {msg && <span className="ml-3 text-sm text-forest/70">{msg}</span>}
      </div>

      {/* Existing assignments */}
      <h2 className="text-xl font-display text-forest mb-3">Recent assignments ({assignments.length})</h2>
      <div className="space-y-2">
        {assignments.length === 0 && <p className="text-sm text-forest/60 italic">No assignments yet.</p>}
        {assignments.map((a) => (
          <div key={a.id} className="bg-cream border border-forest/15 p-3 flex items-center gap-3 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.15em] bg-forest/10 text-forest px-2 py-0.5">{a.status}</span>
            <span className="text-sm font-medium text-forest">{a.scheduled_date}</span>
            {a.time_window && <span className="text-xs uppercase tracking-[0.15em] text-forest/60">{a.time_window}</span>}
            <span className="text-sm text-forest/80">{a.resident_name} → {a.mowkin_label}</span>
          </div>
        ))}
      </div>
      </div>
    </>
  );
}
